import React, { useEffect } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { trackPageview } from '@xingternal/hops-tracking';
import { useExperimentTrackingData, useViewerData } from '@xingternal/hub';

import { DEFAULT_SORT } from '@/domain/search/constants';
import { useSearchQuery } from '@/domain/search/contexts';
import { isVisibleJob } from '@/domain/search/helpers';
import { useSearch } from '@/domain/search/hooks';
import { JobFilterCollection } from '@/graphql/generated';
import { formatOldFilters } from '@/helpers/i18n';
import { unscrambleId, unscrambleStringId } from '@/helpers/xing-one';
import { usePrevious, useTrackNwtViewedScreen } from '@/hooks';
import { useTrackNwtEvent } from '@/hooks/use-nwt-tracking';

type Props = {
  trackAction: string;
  setTrackAction: (action: string) => void;
};

const filterConfig = {
  JobFilterEmploymentType: { property: 'employmentType', trackingName: 'type' },
  JobFilterCareerLevel: { property: 'careerLevel', trackingName: 'level' },
  JobFilterDiscipline: { property: 'discipline', trackingName: 'discipline' },
  JobFilterIndustry: { property: 'industry', trackingName: 'industry' },
  JobFilterBenefitEmployeePerk: { property: 'benefitEmployeePerk', trackingName: 'perks' },
  JobFilterBenefitWorkingCulture: { property: 'benefitWorkingCulture', trackingName: 'atmosphere' },
  JobFilterCountry: { property: 'country', trackingName: 'country' },
  JobFilterCity: { property: 'city', trackingName: 'city' },
  JobFilterRemoteOption: { property: 'remoteOption', trackingName: 'remote' },
} as const;

export const filterCollectionReducer = (
  formatMessage: IntlShape['formatMessage']
): ((acc: string[], filter: JobFilterCollection) => string[]) => {
  let hasCompanyFilter = false;

  return (acc, filter) => {
    if (!filter?.__typename) {
      return acc;
    }

    if (filter.__typename === 'JobFilterCompany') {
      if (!hasCompanyFilter) {
        hasCompanyFilter = true;
        return [...acc, 'companies'];
      }

      return acc;
    }

    if (filter.__typename === 'JobFilterSalary' && typeof filter.min === 'number' && typeof filter.max === 'number') {
      return [...acc, `salary_${filter.min}`, `salary_${filter.max}`];
    }

    if ('entityId' in filter) {
      const { property, trackingName } = filterConfig[filter.__typename];

      // We need to format PropSearchListFilter exactly as the old page, so DA reports work properly
      const filterTrackingCopy =
        property === 'city' || property === 'remoteOption'
          ? filter[property]?.localizationValue
          : formatOldFilters(formatMessage)(trackingName, unscrambleStringId(filter.entityId));

      return [...acc, `${trackingName}_${filterTrackingCopy}`];
    }

    return acc;
  };
};

export const SearchPageTracking: React.VFC<Props> = ({ trackAction, setTrackAction }) => {
  const { page, sort } = useSearchQuery();
  const { loading, data } = useSearch();
  const searchGuid = data?.jobSearchByQuery?.searchQuery.guid;
  const { formatMessage } = useIntl();
  const experimentTrackingData = useExperimentTrackingData();
  const { data: userData } = useViewerData();
  const userId = userData?.user?.id;
  const unscrambledUserId = userId ? unscrambleId(userId) : 0;

  const searchId = `${searchGuid}_${page}_${sort}_${loading}`;
  const previousSearchId = usePrevious(searchId);

  useTrackNwtViewedScreen({ page: 'jobs_search/serp' });
  const trackNwt = useTrackNwtEvent();

  useEffect(() => {
    if (!loading && searchId !== previousSearchId) {
      if (!data?.jobSearchByQuery?.collection) {
        trackPageview('wbm/Search_Stellenmarkt/error');
        trackNwt({
          page: 'jobs_search/error',
          event: 'viewed_screen',
        });
      } else {
        const { collection, searchQuery, total } = data.jobSearchByQuery;

        const jobIds = collection.filter(isVisibleJob).map((item) => unscrambleId(item.jobDetail.id));
        const keywords = searchQuery.body?.keywords;
        const location = searchQuery.body?.location?.city?.name || searchQuery.body?.location?.text;
        const radius = searchQuery.body?.location?.radius;

        const filters = searchQuery.body?.filterCollection?.reduce(filterCollectionReducer(formatMessage), []) ?? [];

        const baseData = {
          PropChannel: 'wbm/Search_Stellenmarkt',
          PropJobsSearchIds: jobIds.join(','),
          ...(trackAction && { PropTrackAction: trackAction }),
          ...(keywords && { PropSearchTerm: keywords }),
          ...(location && { PropSearchCity: location }),
          PropSearchResultAmount: total,
          ...(filters.length > 0 && { PropSearchListFilter: filters.join(';') }),
          PropSortOrder: `jobs_${sort || DEFAULT_SORT}`,
          PropPagination: page,
          brazeUserId: unscrambledUserId,
        };

        const contextData = {
          PropContextDimension1: `jobs_search_filter_radius_${radius || 'no_radius'}`,
          ...experimentTrackingData,
        };

        trackPageview('wbm/Search_Stellenmarkt/serp', baseData, contextData);
        setTrackAction('');
      }
    }
  }, [
    data?.jobSearchByQuery,
    formatMessage,
    loading,
    page,
    previousSearchId,
    searchId,
    setTrackAction,
    sort,
    trackAction,
    unscrambledUserId,
    experimentTrackingData,
    trackNwt,
  ]);

  return null;
};
